// High-throughput particle system for Infinity Breaker.
//
// Structure-of-Arrays pool with typed arrays. Dense-packed via swap-remove on
// death, so iteration cost scales with live count, not capacity. Renders to a
// Uint32 ImageData buffer (single-pixel writes) so we can push hundreds of
// thousands of particles per frame at 60fps.
//
// Particle types share the integration loop. Per-type physics are looked up
// from small Float32 tables. Color is stored as packed ABGR (little-endian),
// matching how the browser interprets the Uint32 view of ImageData on every
// platform we ship to.

export const TYPE_SPARK = 0;
export const TYPE_FRAGMENT = 1;
export const TYPE_EMBER = 2;
export const TYPE_SHIMMER = 3;
export const TYPE_TRAIL = 4;

const TYPE_COUNT = 5;

// Per-type physics tables. Indexed by TYPE_*.
const GRAVITY = new Float32Array(TYPE_COUNT);
const DRAG = new Float32Array(TYPE_COUNT);
GRAVITY[TYPE_SPARK] = 280;   DRAG[TYPE_SPARK] = 0.985;
GRAVITY[TYPE_FRAGMENT] = 620; DRAG[TYPE_FRAGMENT] = 0.992;
GRAVITY[TYPE_EMBER] = -55;    DRAG[TYPE_EMBER] = 0.965;
GRAVITY[TYPE_SHIMMER] = 0;    DRAG[TYPE_SHIMMER] = 0.94;
GRAVITY[TYPE_TRAIL] = 90;     DRAG[TYPE_TRAIL] = 0.95;

/** Replace alpha byte of a packed ABGR color. */
export function withAlpha(color: number, alpha: number): number {
  return ((color & 0x00ffffff) | ((alpha & 0xff) << 24)) >>> 0;
}

/** Perturb each RGB channel by ±amount (uniform). Alpha preserved. */
export function jitterColor(color: number, amount: number): number {
  const a = (color >>> 24) & 0xff;
  const r = color & 0xff;
  const g = (color >>> 8) & 0xff;
  const b = (color >>> 16) & 0xff;
  const jr = clamp8(r + ((Math.random() * 2 - 1) * amount) | 0);
  const jg = clamp8(g + ((Math.random() * 2 - 1) * amount) | 0);
  const jb = clamp8(b + ((Math.random() * 2 - 1) * amount) | 0);
  return ((a << 24) | (jb << 16) | (jg << 8) | jr) >>> 0;
}

/** Pack `#rrggbb` into 0xAABBGGRR (Uint32 little-endian = canvas RGBA order). */
export function hexToABGR(hex: string, alpha = 0xff): number {
  // Accept '#rgb' and '#rrggbb'.
  let s = hex.startsWith('#') ? hex.slice(1) : hex;
  if (s.length === 3) s = s[0]! + s[0]! + s[1]! + s[1]! + s[2]! + s[2]!;
  const v = parseInt(s, 16);
  const r = (v >> 16) & 0xff;
  const g = (v >> 8) & 0xff;
  const b = v & 0xff;
  return ((alpha & 0xff) << 24) | (b << 16) | (g << 8) | r;
}

/** HSL → packed ABGR, alpha 0xff. Mirrors brickHueColor's `hsl(h,80%,62%)`. */
export function hslToABGR(h: number, s: number, l: number, alpha = 0xff): number {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = (h % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp < 1) { r = c; g = x; }
  else if (hp < 2) { r = x; g = c; }
  else if (hp < 3) { g = c; b = x; }
  else if (hp < 4) { g = x; b = c; }
  else if (hp < 5) { r = x; b = c; }
  else { r = c; b = x; }
  const m = ln - c / 2;
  const R = Math.round((r + m) * 255);
  const G = Math.round((g + m) * 255);
  const B = Math.round((b + m) * 255);
  return ((alpha & 0xff) << 24) | (B << 16) | (G << 8) | R;
}

export class ParticleSystem {
  readonly cap: number;
  count = 0;

  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  readonly life: Float32Array;
  readonly invMaxLife: Float32Array;
  readonly color: Uint32Array;
  readonly type: Uint8Array;
  readonly size: Uint8Array;

  constructor(cap: number) {
    this.cap = cap;
    this.x = new Float32Array(cap);
    this.y = new Float32Array(cap);
    this.vx = new Float32Array(cap);
    this.vy = new Float32Array(cap);
    this.life = new Float32Array(cap);
    this.invMaxLife = new Float32Array(cap);
    this.color = new Uint32Array(cap);
    this.type = new Uint8Array(cap);
    this.size = new Uint8Array(cap);
  }

  clear(): void {
    this.count = 0;
  }

  /** Drop oldest by index range — for hard caps when callers must spawn. */
  private spawn(
    type: number, x: number, y: number, vx: number, vy: number,
    life: number, color: number, size: number,
  ): void {
    if (this.count >= this.cap) return; // hard cap: silently drop new spawns
    const i = this.count++;
    this.x[i] = x; this.y[i] = y;
    this.vx[i] = vx; this.vy[i] = vy;
    this.life[i] = life;
    this.invMaxLife[i] = life > 0 ? 1 / life : 0;
    this.color[i] = color;
    this.type[i] = type;
    this.size[i] = size;
  }

  /** Generic emitter — exposed for cases the helpers don't cover. */
  emit(
    type: number, x: number, y: number, vx: number, vy: number,
    life: number, color: number, size = 1,
  ): void {
    this.spawn(type, x, y, vx, vy, life, color, size);
  }

  /**
   * Sparks: fast outward burst. Peak alpha and life are randomized per
   * particle so a burst reads as a soft cloud, not a uniform ring. Wide life
   * spread means trailing sparks linger after the hot core fades.
   */
  spawnSparks(x: number, y: number, color: number, count = 12, speed = 180): void {
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = speed * (0.25 + Math.random() * 1.1);
      const peakA = 0x70 + ((Math.random() * 0x80) | 0);
      const col = withAlpha(jitterColor(color, 28), peakA);
      this.spawn(
        TYPE_SPARK, x, y,
        Math.cos(a) * sp, Math.sin(a) * sp,
        0.35 + Math.random() * 1.1,
        col, Math.random() < 0.18 ? 3 : Math.random() < 0.55 ? 2 : 1,
      );
    }
  }

  /**
   * Brick shatter — fills the brick rectangle with a dense grid of fragments
   * that explode outward from the center. Fragment count scales with brick
   * area; the goal is "this thing fractured into pixels", not "puff of smoke".
   */
  spawnBrickShatter(
    bx: number, by: number, bw: number, bh: number,
    baseColor: number, density = 1,
  ): void {
    const cx = bx + bw / 2;
    const cy = by + bh / 2;
    const cols = Math.max(6, Math.floor(bw * 0.6 * density));
    const rows = Math.max(3, Math.floor(bh * 0.55 * density));
    const baseR = baseColor & 0xff;
    const baseG = (baseColor >> 8) & 0xff;
    const baseB = (baseColor >> 16) & 0xff;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const fx = bx + (c + 0.5) / cols * bw + (Math.random() - 0.5) * 1.2;
        const fy = by + (r + 0.5) / rows * bh + (Math.random() - 0.5) * 1.2;
        const dx = fx - cx;
        const dy = fy - cy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const outX = dx / dist;
        const outY = dy / dist;
        const sp = 70 + Math.random() * 260;
        // Slight color jitter so the shatter doesn't look flat.
        const j = (Math.random() * 40 - 20) | 0;
        const cr = clamp8(baseR + j);
        const cg = clamp8(baseG + j);
        const cb = clamp8(baseB + j);
        const col = (0xff << 24) | (cb << 16) | (cg << 8) | cr;
        const peakA = 0x90 + ((Math.random() * 0x60) | 0);
        const colA = withAlpha(col, peakA);
        this.spawn(
          TYPE_FRAGMENT, fx, fy,
          outX * sp + (Math.random() - 0.5) * 40,
          outY * sp - 60 + (Math.random() - 0.5) * 40,
          0.7 + Math.random() * 1.0,
          colA, Math.random() < 0.18 ? 3 : Math.random() < 0.6 ? 2 : 1,
        );
      }
    }
  }

  /** Embers: slow upward drift, long life — for explosions and big clears. */
  spawnEmbers(x: number, y: number, color: number, count = 40, speed = 220): void {
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = speed * (0.15 + Math.random() * 1.1);
      const peakA = 0x50 + ((Math.random() * 0x80) | 0);
      const col = withAlpha(jitterColor(color, 36), peakA);
      this.spawn(
        TYPE_EMBER, x, y,
        Math.cos(a) * sp,
        Math.sin(a) * sp - 40,
        0.9 + Math.random() * 1.8,
        col, Math.random() < 0.15 ? 3 : Math.random() < 0.55 ? 2 : 1,
      );
    }
  }

  /** Pickup shimmer trail — single particle per call. */
  spawnShimmer(x: number, y: number, color: number): void {
    const peakA = 0x40 + ((Math.random() * 0x70) | 0);
    const col = withAlpha(jitterColor(color, 30), peakA);
    this.spawn(
      TYPE_SHIMMER, x, y,
      (Math.random() - 0.5) * 30,
      30 + Math.random() * 40,
      0.55 + Math.random() * 0.75,
      col, Math.random() < 0.3 ? 1 : 2,
    );
  }

  /** Ball trail dot. */
  spawnTrail(x: number, y: number, color: number): void {
    const peakA = 0x60 + ((Math.random() * 0x70) | 0);
    const col = withAlpha(jitterColor(color, 24), peakA);
    this.spawn(
      TYPE_TRAIL, x, y,
      (Math.random() - 0.5) * 20,
      (Math.random() - 0.5) * 20,
      0.3 + Math.random() * 0.4,
      col, 1,
    );
  }

  /**
   * Fused step + render. Iterates each live particle once, advances physics,
   * swap-removes dead/offscreen entries, and plots survivors into the device
   * pixel buffer in the same pass. Two reasons this is faster than calling
   * `update` then `render`:
   *   1. Halves the array iterations across the SoA pool.
   *   2. Keeps freshly-read values in registers for the render step instead
   *      of re-fetching from typed arrays.
   * `dt == 0` freezes physics but still renders (used while paused).
   */
  stepAndRender(
    dt: number, worldH: number,
    buf: Uint32Array, w: number, h: number,
    scale: number, ox: number, oy: number,
  ): void {
    const { x, y, vx, vy, life, invMaxLife, color, type, size } = this;
    let i = 0;
    let n = this.count;
    const yCullBelow = worldH + 24;
    while (i < n) {
      const oldLife = life[i] as number;
      const newLife = oldLife - dt;
      const oldY = y[i] as number;
      if (newLife <= 0 || oldY > yCullBelow) {
        const last = --n;
        if (i !== last) {
          x[i] = x[last] as number; y[i] = y[last] as number;
          vx[i] = vx[last] as number; vy[i] = vy[last] as number;
          life[i] = life[last] as number; invMaxLife[i] = invMaxLife[last] as number;
          color[i] = color[last] as number;
          type[i] = type[last] as number;
          size[i] = size[last] as number;
        }
        continue;
      }
      // Physics
      const t = type[i] as number;
      const g = GRAVITY[t] as number;
      const d = DRAG[t] as number;
      const vxi = vx[i] as number;
      const vyi = vy[i] as number;
      const newX = (x[i] as number) + vxi * dt;
      const newY = oldY + vyi * dt;
      x[i] = newX;
      y[i] = newY;
      vx[i] = vxi * d;
      vy[i] = (vyi + g * dt) * d;
      life[i] = newLife;

      // Render
      const lt = newLife * (invMaxLife[i] as number);
      if (lt > 0) {
        const px = (newX * scale + ox) | 0;
        const py = (newY * scale + oy) | 0;
        if (px >= 0 && px < w && py >= 0 && py < h) {
          const ci = color[i] as number;
          const peakA = (ci >>> 24) & 0xff;
          const fade = lt >= 1 ? 1 : lt * lt * (3 - 2 * lt);
          const a = (peakA * fade) | 0;
          if (a >= 4) {
            const c = ((ci & 0x00ffffff) | (a << 24)) >>> 0;
            const s = size[i] as number;
            if (s <= 1) {
              buf[py * w + px] = c;
            } else {
              const half = s >> 1;
              const x0 = px - half < 0 ? 0 : px - half;
              const y0 = py - half < 0 ? 0 : py - half;
              const x1Cand = px - half + s - 1;
              const y1Cand = py - half + s - 1;
              const x1 = x1Cand >= w ? w - 1 : x1Cand;
              const y1 = y1Cand >= h ? h - 1 : y1Cand;
              for (let yy = y0; yy <= y1; yy++) {
                const row = yy * w;
                for (let xx = x0; xx <= x1; xx++) buf[row + xx] = c;
              }
            }
          }
        }
      }
      i++;
    }
    this.count = n;
  }

}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}
